import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { clients as clientsApi, campaigns, getCampaignId } from '../api/client.js';
import { SearchIcon, UsersIcon, CheckIcon, ClockIcon, AlertIcon, ChevronIcon } from '../common/icons.jsx';
import ClientFilters from './ClientFilters.jsx';

const STATUS_CONFIG = {
  pending: { label: 'En attente', border: 'border-l-gray-400', badge: 'bg-gray-100 text-gray-700', Icon: ClockIcon },
  in_progress: { label: 'En cours', border: 'border-l-orange-400', badge: 'bg-orange-50 text-orange-700', Icon: ClockIcon },
  delivered: { label: 'Livré', border: 'border-l-emerald-500', badge: 'bg-emerald-50 text-emerald-700', Icon: CheckIcon },
  problem: { label: 'Problème', border: 'border-l-red-500', badge: 'bg-red-50 text-red-700', Icon: AlertIcon },
};

export default function ClientList() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const cid = getCampaignId();
    if (!cid) return;
    campaigns.listUsers(cid)
      .then((users) => setDrivers(users.filter((u) => u.role === 'driver')))
      .catch(() => setDrivers([]));
    clientsApi.getCities()
      .then(setCities)
      .catch(() => setCities([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (driverFilter) params.driverId = driverFilter;
      if (cityFilter) params.city = cityFilter;
      setClients(await clientsApi.list(params));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, driverFilter, cityFilter]);

  // Recharge debouncee : couvre recherche texte ET filtres. `load` est memoise
  // sur ses entrees, donc l effet capture toujours la closure a jour. Corrige
  // le bug : l ancien handler en useCallback([]) figeait `load`/`search` au
  // premier render et envoyait toujours une recherche vide.
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  }, [load]);

  const handleSearchChange = (val) => setSearch(val);

  const stats = {
    total: clients.length,
    delivered: clients.filter(c => c.delivery_status === 'delivered').length,
    pending: clients.filter(c => c.delivery_status === 'pending').length,
    problem: clients.filter(c => c.delivery_status === 'problem').length,
  };

  return (
    <div className="animate-fade-in">
      <h2 className="text-xl font-bold mb-4 text-[var(--color-primary)]">Clients</h2>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-2 mb-5">
        <StatPill Icon={UsersIcon} value={stats.total} label="Total" color="text-[var(--color-accent)]" bg="bg-blue-50" />
        <StatPill Icon={CheckIcon} value={stats.delivered} label="Livrés" color="text-[var(--color-success)]" bg="bg-emerald-50" />
        <StatPill Icon={ClockIcon} value={stats.pending} label="Attente" color="text-[var(--color-neutral)]" bg="bg-gray-100" />
        <StatPill Icon={AlertIcon} value={stats.problem} label="Problèmes" color="text-[var(--color-error)]" bg="bg-red-50" />
      </div>

      {/* Search */}
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative mb-3">
        <SearchIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un client..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)] focus:outline-none"
        />
      </form>

      <ClientFilters
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        driverFilter={driverFilter}
        setDriverFilter={setDriverFilter}
        cityFilter={cityFilter}
        setCityFilter={setCityFilter}
        drivers={drivers}
        cities={cities}
      />

      {error && (
        <div className="flex items-center gap-2 text-[var(--color-error)] bg-red-50 rounded-xl px-4 py-2.5 text-sm mb-4">
          <AlertIcon size={16} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="w-7 h-7 border-3 border-gray-200 border-t-[var(--color-accent)] rounded-full animate-spin mx-auto" />
        </div>
      ) : clients.length === 0 ? (
        <p className="text-[var(--color-neutral)] text-center py-10 text-sm">Aucun client. Importez un fichier Excel.</p>
      ) : (
        <div className="space-y-2">
          {clients.map((client) => {
            const cfg = STATUS_CONFIG[client.delivery_status] || STATUS_CONFIG.pending;
            return (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className={`block bg-white rounded-xl p-4 shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] border-l-4 ${cfg.border}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[var(--color-primary)] truncate">{client.name}</div>
                    <div className="text-sm text-[var(--color-neutral)] truncate">{client.city || client.address}</div>
                    {client.nb_units > 0 && (
                      <div className="text-xs text-gray-400 mt-0.5">{client.nb_units} unités</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-3 shrink-0">
                    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${cfg.badge}`}>
                      <cfg.Icon size={12} />
                      {cfg.label}
                    </span>
                    <ChevronIcon size={16} className="text-gray-300" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Compact stat pill for the stats bar.
 * @param {{ Icon: Function, value: number, label: string, color: string, bg: string }} props
 */
function StatPill({ Icon, value, label, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-2.5 text-center`}>
      <Icon size={14} className={`${color} mx-auto mb-1`} />
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-[var(--color-neutral)]">{label}</div>
    </div>
  );
}
